import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Megaphone, Users, CalendarCheck, ShieldCheck, Upload, Eye, CreditCard, CheckCircle2 } from "lucide-react";

const STEPS = [
  { icon: Users, t: "Register", d: "Create your free organizer account in seconds." },
  { icon: Upload, t: "Create Race", d: "Add categories, schedule, medal & T-shirt photos." },
  { icon: Eye, t: "Preview", d: "See exactly how your race page will look." },
  { icon: CheckCircle2, t: "Submit for Review", d: "Send your listing to our team for approval." },
  { icon: CheckCircle2, t: "Get Published", d: "Once approved, your race goes live to athletes." },
];

export default function ForOrganizers() {
  const { user } = useAuth();
  const cta = user?.role === "organizer" || user?.role === "admin" ? "/organizer" : "/organizer/register";
  return (
    <Layout>
      <section className="relative bg-[#0a1420] overflow-hidden">
        <img src="https://images.pexels.com/photos/29840338/pexels-photo-29840338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="font-display font-semibold text-[#22d3ee] tracking-[0.25em] text-sm uppercase">For Organizers</span>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl text-white leading-none mt-3">List Your Race</h1>
          <p className="text-slate-200 text-lg mt-4 max-w-xl">Reach thousands of runners and athletes across Malaysia who are actively looking for their next event.</p>
          <div className="flex flex-wrap gap-6 mt-8">
            {[[Users, "Right Audience", "athletes ready to race"], [CalendarCheck, "Simple", "submission process"], [ShieldCheck, "Supported", "by our team"]].map(([Icon, a, b], i) => (
              <div key={i} className="flex items-center gap-3 text-white">
                <Icon size={22} className="text-[#22d3ee]" /><div><div className="font-bold font-mono-data">{a}</div><div className="text-xs text-slate-300">{b}</div></div>
              </div>
            ))}
          </div>
          <Link to={cta} data-testid="organizer-hero-cta" className="inline-flex items-center gap-2 mt-8 bg-[#22d3ee] text-[#0a1420] font-bold px-7 py-3.5 rounded-lg hover:bg-white transition-colors">List Your Race <ArrowRight size={18} /></Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display font-extrabold uppercase tracking-wide text-3xl text-[#0f172a] text-center">How It Works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-10">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 text-center relative">
                <span className="absolute top-3 right-4 font-display font-black text-4xl text-slate-100">{i + 1}</span>
                <div className="w-12 h-12 rounded-lg bg-[#0a1420] text-[#22d3ee] grid place-items-center mx-auto"><Icon size={22} /></div>
                <h3 className="font-display font-bold uppercase text-base mt-4 text-slate-800">{s.t}</h3>
                <p className="text-sm text-slate-500 mt-1">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-100 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-[#0a1420] rounded-2xl p-10">
            <p className="font-display font-semibold text-[#22d3ee] uppercase tracking-widest text-sm">List Your Race</p>
            <div className="font-display font-black text-white text-4xl sm:text-5xl mt-3 leading-tight">Reach athletes across<br />Malaysia &amp; Singapore</div>
            <p className="text-slate-300 mt-4">Create your organizer account and submit your race — full race page, medal &amp; merchandise showcase, categories, schedule and share tools. Our team will be in touch with listing details.</p>
            <Link to={cta} className="inline-flex items-center gap-2 mt-6 bg-[#22d3ee] text-[#0a1420] font-bold px-7 py-3.5 rounded-lg hover:bg-white transition-colors">Get Started <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
